import { useQuery } from '@tanstack/react-query';
import * as Sentry from '@sentry/react-native';
import { supabase, isShortId, toError } from '../supabase';
import { generateDigestSlug } from '../paperUrl';
import { PaperData } from '../../components/papers/types';
import { PRIVATE_OR_NOT_FOUND, toPrivateOrNotFoundError } from '../accessErrors';
import {
    fetchCurrentEntitlements,
    invalidateEntitlementSnapshotCache,
} from '../entitlements';
import { classifyBillingPreviewDigestAccess } from '../billingPreviewState';
import { fetchPublicDigestPreviewAccess } from '../publicDigestPreviewAccess';

interface DigestPaper {
    rank_position: number;
    project_paper_id: string;
    project_paper: {
        id: string;
        short_id: string | null;
        slug: string | null;
        paper_title: string | null;
        summary: string | null;
        relevance_score: number | null;
        relevance_class: number | null;
        matching_details: any;
        personalized_note: string | null;
        paper: PaperData;
    };
}

export interface DigestIssue {
    id: string;
    short_id?: string;
    created_at: string;
    sent_at: string | null;
    status: string | null;
    email_sent: boolean;
    digest_type: string | null;
    project: {
        id: string;
        name: string;
        visibility: string;
        user_id: string | null;
        paused_at: string | null;
    };
    papers: DigestPaper[];
    papers_total_count: number;
    preview_access: 'restricted' | 'unrestricted';
    preview_trial_path: string | null;
}

/**
 * Hook to fetch a digest by ID (short_id or full UUID).
 *
 * @param issueId - The digest ID (8-char short_id or full UUID)
 * @param urlSlug - Optional URL slug for collision resolution when using short_id
 */
export const useDigestQuery = (
    issueId: string | undefined,
    urlSlug?: string | null,
    options: {
        enabled?: boolean;
        viewerUserId?: string | null;
    } = {}
) => {
    return useQuery({
        queryKey: [
            'digest',
            issueId,
            urlSlug,
            options.viewerUserId || 'anonymous',
        ],
        queryFn: async ({ signal }) => {
            if (!issueId) throw new Error('No digest issue ID');

            const selectFields = `
                id,
                short_id,
                created_at,
                sent_at,
                status,
                email_sent,
                digest_type,
                project:project_id (
                    id,
                    name,
                    visibility,
                    user_id,
                    paused_at
                )
            `;

            let digestData: any = null;

            if (isShortId(issueId)) {
                // Use collision-aware query for short_ids
                const { data, error } = await supabase
                    .from('email_digest')
                    .select(selectFields)
                    .eq('short_id', issueId)
                    .order('created_at', { ascending: true });

                if (error) {
                    Sentry.captureException(toError(error), {
                        extra: { issueId, urlSlug, table: 'email_digest' }
                    });
                    throw toError(error);
                }

                if (!data || data.length === 0) {
                    Sentry.captureMessage('Digest not found in useDigestQuery', {
                        level: 'info',
                        extra: { issueId, urlSlug }
                    });
                    throw new Error(PRIVATE_OR_NOT_FOUND);
                }

                if (data.length === 1) {
                    digestData = data[0];
                } else {
                    // Collision detected
                    Sentry.captureMessage('Short-ID collision detected in useDigestQuery', {
                        level: 'warning',
                        extra: { issueId, urlSlug, matchCount: data.length }
                    });

                    // Try slug matching (slug is derived from project.name)
                    if (urlSlug) {
                        const slugMatch = data.find((d: any) => {
                            if (d.project?.name) {
                                return generateDigestSlug(d.project.name) === urlSlug;
                            }
                            return false;
                        });
                        if (slugMatch) {
                            digestData = slugMatch;
                        }
                    }

                    // Fallback: use oldest record
                    if (!digestData) {
                        digestData = data[0];
                    }
                }
            } else {
                // Full UUID - use direct query
                const { data, error: digestError } = await supabase
                    .from('email_digest')
                    .select(selectFields)
                    .eq('id', issueId)
                    .limit(1);

                if (digestError) {
                    const accessError = toPrivateOrNotFoundError(digestError);
                    if (accessError) {
                        Sentry.captureMessage('Digest not found in useDigestQuery', {
                            level: 'info',
                            extra: { issueId, urlSlug }
                        });
                        throw accessError;
                    }
                    Sentry.captureException(digestError, {
                        extra: { issueId, urlSlug }
                    });
                    throw toError(digestError);
                }

                digestData = data?.[0] || null;
            }

            if (!digestData || !digestData.project) {
                Sentry.captureMessage('Digest or project not found in useDigestQuery', {
                    level: 'info',
                    extra: { issueId, urlSlug, hasDigestData: !!digestData, hasProject: !!digestData?.project }
                });
                throw new Error(PRIVATE_OR_NOT_FOUND);
            }

            // Step 2: Check access permissions
            const { data: { session } } = await supabase.auth.getSession();
            const isOwner = digestData.project.user_id === session?.user?.id;
            const isPublic = digestData.project.visibility === 'public';

            if (!isOwner && !isPublic) {
                throw new Error(PRIVATE_OR_NOT_FOUND);
            }

            let previewAccess: 'restricted' | 'unrestricted' = 'unrestricted';
            let publicPreviewVisibleIds: string[] | null = null;
            let authoritativeTotalCount: number | null = null;
            let previewTrialPath: string | null = null;
            if (isPublic) {
                const publicAccess = await fetchPublicDigestPreviewAccess(digestData.id, signal);
                if (publicAccess.project_id !== digestData.project.id) {
                    throw new Error('Paperzilla returned mismatched public preview access.');
                }
                previewAccess = publicAccess.preview_access;
                publicPreviewVisibleIds = publicAccess.visible_project_paper_ids;
                authoritativeTotalCount = publicAccess.papers_total_count;
                previewTrialPath = publicAccess.trial_path;
            } else if (isOwner) {
                if (!session?.access_token || !session.user?.id) {
                    throw new Error('Could not verify preview access. Please reload and try again.');
                }

                invalidateEntitlementSnapshotCache(session.user.id);
                const snapshot = await fetchCurrentEntitlements(session.access_token, {
                    forceRefresh: true,
                    userId: session.user.id,
                });
                const classification = classifyBillingPreviewDigestAccess(
                    snapshot,
                    digestData.id
                );
                if (classification === 'restricted') {
                    previewAccess = 'restricted';
                } else if (classification === 'unrestricted') {
                    previewAccess = 'unrestricted';
                } else {
                    throw new Error('Could not verify preview access. Please reload and try again.');
                }
            }

            // Step 3: Fetch papers in this digest issue
            let papersQuery = supabase
                .from('digest_papers')
                .select(`
                    project_paper_id,
                    rank_position,
                    project_paper:project_paper_id (
                        id,
                        short_id,
                        slug,
                        paper_title,
                        summary,
                        relevance_score,
                        relevance_class,
                        matching_details,
                        personalized_note,
                        paper:paper_id (
                            id,
                            title,
                            abstract,
                            authors,
                            published_date,
                            pdf_url,
                            url,
                            doi,
                            source_id,
                            source_paper_id
                        )
                    )
                `, { count: 'exact' })
                .eq('digest_id', digestData.id)
                .order('rank_position', { ascending: true });
            if (previewAccess === 'restricted' && publicPreviewVisibleIds) {
                papersQuery = publicPreviewVisibleIds.length > 0
                    ? papersQuery.in('project_paper_id', publicPreviewVisibleIds).limit(3)
                    : papersQuery.limit(0);
            } else if (previewAccess === 'restricted') {
                papersQuery = papersQuery.limit(3);
            }

            const { data: digestPapers, error: papersError, count: papersTotalCount } = await papersQuery;

            if (papersError) {
                throw toError(papersError);
            }

            return {
                ...digestData,
                papers: digestPapers || [],
                papers_total_count: authoritativeTotalCount
                    ?? papersTotalCount
                    ?? digestPapers?.length
                    ?? 0,
                preview_access: previewAccess,
                preview_trial_path: previewTrialPath,
            } as DigestIssue;
        },
        enabled: (options.enabled ?? true) && !!issueId,
        // Access can transition while a digest remains cached (for example
        // when a one-time preview pauses). Always reclassify before showing a
        // retained unrestricted result.
        staleTime: 0,
        refetchOnMount: 'always',
        gcTime: 1000 * 60 * 30,   // Keep in cache for 30 minutes
    });
};
